import React from 'react';
import { AtomicBlockUtils, EditorState, Modifier, RichUtils, DefaultDraftBlockRenderMap } from 'draft-js';
import linkifyIt from 'linkify-it';
import tlds from 'tlds';
import { OrderedSet, Map } from 'immutable';
import { BLOCK_TYPE, INLINE_TYPE } from './rendererHelpers';
import { pasteTextTable } from './tableHelper';
import TableWrapper from '../blocks/TableWrapper';
import AtomicItem from '../blocks/AtomicItem';
import { THEME_TYPE_DARK } from 'constants/ThemeSetting';

export const REGEXP_MENTION = /\[(@[\wа-яА-ЯёЁ\s-_@.]+)]/g;

// Extension for links detecting
export const linkify = linkifyIt();
linkify.tlds(tlds);

export const LIGHT_SELECTION_COLORS = {
  SELECTION_YELLOW: {
    backgroundColor: '#fdf45f',
  },
  SELECTION_GREEN: {
    backgroundColor: '#c4ffa6',
  },
  SELECTION_BLUE: {
    backgroundColor: '#b7dde8',
  },
  SELECTION_ORANGE: {
    backgroundColor: '#fbd5b5',
  },
  SELECTION_PINK: {
    backgroundColor: '#f7cde6',
  },
  SELECTION_PURPLE: {
    backgroundColor: '#cfcdf7',
  },
  SELECTION_GREY: {
    backgroundColor: '#e5e5e5',
  },
};

export const DARK_SELECTION_COLORS = {
  SELECTION_YELLOW: {
    backgroundColor: 'rgb(153, 144, 0)',
  },
  SELECTION_GREEN: {
    backgroundColor: 'rgb(96, 155, 66)',
  },
  SELECTION_BLUE: {
    backgroundColor: 'rgb(83, 121, 132)',
  },
  SELECTION_ORANGE: {
    backgroundColor: 'rgb(151, 113, 81)',
  },
  SELECTION_PINK: {
    backgroundColor: 'rgb(147, 105, 130)',
  },
  SELECTION_PURPLE: {
    backgroundColor: 'rgb(107, 105, 147)',
  },
  SELECTION_GREY: {
    backgroundColor: 'rgb(129, 129, 129)',
  },
};

// Configuration of style for custom inline styles.
export const inlineStyleMap = {
  STRIKETHROUGH: {
    textDecoration: 'line-through',
  },
  MENTION: {
    fontStyle: 'bold',
    color: '#78be20',
  },
  LINK: {
    color: '#78be20',
  },
};

export const prepareCustomStyle = (inlineMap, theme) => {
  const selectionColors = theme === THEME_TYPE_DARK ? DARK_SELECTION_COLORS : LIGHT_SELECTION_COLORS;

  return {
    ...inlineMap,
    ...selectionColors,
  };
};

// Function returns class names for certain blocks
export const getBlockStyle = (block) => {
  switch (block.getType()) {
    case 'blockquote':
      return 'editor-blockquote';
    case 'unstyled':
      return 'editor-paragraph-block';
    case 'unordered-list-item':
    case 'ordered-list-item':
      return 'editor-list-item';
    default:
      return null;
  }
};

export const getBlockRenderMap = (editorState) =>
  DefaultDraftBlockRenderMap.merge(
    Map({
      [BLOCK_TYPE.TABLE_ROW_ITEM]: {
        element: 'td',
        wrapper: <TableWrapper editorState={editorState} />,
      },
    }),
  );

// Function handles custom blocks rendering
export const blockRendererFn = (block, getFileUrl) => {
  const type = block.getType();
  if (type === 'atomic') {
    return {
      component: AtomicItem,
      editable: false,
      props: {
        getFileUrl,
      },
    };
  }
};

// Function searching specified text fragments in content block
export const findWithRegex = (regex, contentBlock, callback) => {
  // Get text of current content block
  const text = contentBlock.getText();

  let matchArr, start;
  while ((matchArr = regex.exec(text)) !== null) {
    start = matchArr.index;

    // Call decorator handler
    callback(start, start + matchArr[0].length);
  }
};

/**
 * Function handles filtration of characters in content block to determine hyperlinks ranges.
 * @param character Character metadata
 * @param contentState Editor's content state
 * @returns {boolean}
 */
export const filterHyperlinkCharacters = (character, contentState) => {
  // Get entity key of current character
  const entityKey = character.getEntity();
  if (entityKey) {
    // If entity key exists, try to load entity from content state
    const entity = contentState.getEntity(entityKey);

    if (entity) {
      return entity.getType() === INLINE_TYPE.HYPERLINK;
    }
  }
  return false;
};

// Function handles mention entity adding to editor state
export const addMention = ({ editorState, start, end, mentionText, mentionData }) => {
  // Get current content block
  const currentContent = editorState.getCurrentContent();

  // Create new entity
  const contentWithEntity = currentContent.createEntity('MENTION', 'IMMUTABLE', mentionData);
  const entityKey = contentWithEntity.getLastCreatedEntityKey();

  // Get current selection
  const currentSelection = editorState.getSelection();
  const mentionTextSelection = currentSelection.merge({
    anchorOffset: start,
    focusOffset: end,
  });

  // Set up content to insert in editor state
  let insertingContent = Modifier.replaceText(
    contentWithEntity,
    mentionTextSelection,
    ' [' + mentionText + '] ',
    new OrderedSet(/*['MENTION']*/),
    entityKey,
  );

  // Update inserting content
  const blockKey = mentionTextSelection.getAnchorKey();
  const blockSize = editorState
    .getCurrentContent()
    .getBlockForKey(blockKey)
    .getLength();
  if (blockSize === end) {
    insertingContent = Modifier.insertText(insertingContent, insertingContent.getSelectionAfter(), '');
  }

  // Update editor content
  let newEditorState = EditorState.push(editorState, insertingContent, 'insert-fragment');
  // Update editor selection to place it right after inserted mention
  newEditorState = EditorState.forceSelection(newEditorState, insertingContent.getSelectionAfter());
  // Toggle inline style to disable mention style
  return newEditorState;
  // return RichUtils.toggleInlineStyle(newEditorState, 'MENTION');
};

/**
 * Function handles hyperlink entity adding to the editor state.
 * @param editorState
 * @param link
 * @param text
 * @param start
 * @param end
 * @returns {EditorState}
 */
export const commitHyperlink = ({ editorState, link, text, start, end }) => {
  const currentContent = editorState.getCurrentContent();
  const contentWithEntity = currentContent.createEntity(INLINE_TYPE.HYPERLINK, 'MUTABLE', {
    url: link,
  });
  const entityKey = contentWithEntity.getLastCreatedEntityKey();

  // Get current editor selection
  let currentSelection = editorState.getSelection();

  // Get current selection ranges
  let selectionStart = currentSelection.getAnchorOffset();
  let selectionEnd = currentSelection.getFocusOffset();
  if (currentSelection.getIsBackward()) {
    selectionStart = currentSelection.getFocusOffset();
    selectionEnd = currentSelection.getAnchorOffset();
  }

  if (start && end && (start !== selectionStart || end !== selectionEnd)) {
    currentSelection = currentSelection.merge({
      anchorOffset: start,
      focusOffset: end,
    });
  }

  // Update content
  const contentStateWithLink = Modifier.replaceText(
    contentWithEntity,
    currentSelection,
    text,
    new OrderedSet(),
    entityKey,
  );

  // Update editor state
  let newEditorState = EditorState.push(editorState, contentStateWithLink, 'insert-fragment');
  return EditorState.forceSelection(newEditorState, contentStateWithLink.getSelectionAfter());
};

/**
 * Function handles removing of a hyperlink from text fragment on current selection.
 * @param editorState
 * @param entityKey
 * @returns {EditorState}
 */
export const dismissHyperlink = (editorState, entityKey) => {
  const currentContent = editorState.getCurrentContent();
  const currentSelection = editorState.getSelection();

  let linkStart, linkEnd;

  // Find dismissing hyperlink ranges in current block
  const currentBlock = currentContent.getBlockForKey(currentSelection.getAnchorKey());
  currentBlock.findEntityRanges(
    (character) => {
      const key = character.getEntity();
      return key === entityKey;
    },
    (start, end) => {
      linkStart = start;
      linkEnd = end;
    },
  );

  // Get current selection ranges
  let selectionStart = currentSelection.getAnchorOffset();
  let selectionEnd = currentSelection.getFocusOffset();
  if (currentSelection.getIsBackward()) {
    selectionStart = currentSelection.getFocusOffset();
    selectionEnd = currentSelection.getAnchorOffset();
  }

  // Check if we need to update current selection of hyperlink
  let hyperlinkSelection = currentSelection;
  if (selectionStart !== linkStart || selectionEnd !== linkEnd) {
    // Create new selection
    hyperlinkSelection = currentSelection.merge({
      anchorOffset: linkStart,
      focusOffset: linkEnd,
    });
  }

  // Create new content without entity and update editor's state
  const newContentState = Modifier.applyEntity(currentContent, hyperlinkSelection, null);
  const newEditorState = EditorState.push(editorState, newContentState, 'insert-fragment');

  return EditorState.forceSelection(newEditorState, newContentState.getSelectionAfter());
};

/**
 * Function returns bounding client rect of current selection.
 * @returns {DOMRect}
 */
export const getSelectionRect = () => {
  // Get window selection and it's range
  const windowSelection = window.getSelection();
  const selectionRange = windowSelection.getRangeAt(0);

  let rangeRect;
  if (windowSelection.isCollapsed && selectionRange.startContainer instanceof HTMLElement) {
    // If selection is collapsed it means it can be the beginning of editor block
    // which means that we need to process start container in order to get correct client rect coordinates
    rangeRect = selectionRange.startContainer.getBoundingClientRect();
  } else {
    // Get bounding client rect for current window selection
    rangeRect = selectionRange.cloneRange().getBoundingClientRect();
  }

  return rangeRect;
};

/**
 * Function prepares draft editor to save.
 * It creates new link entities if some of them are missing (in case of batch pasting list of links)
 *
 * @param content
 * @param filesList
 * @returns {{entityMap: {}, blocks: []}}
 */
export const prepareContentToSave = (content, filesList) => {
  const { blocks, entityMap } = content;
  let nextEntityIndex = 0;
  let newBlocks = [];
  let newEntityMap = {};

  if (entityMap) {
    newEntityMap = {
      ...entityMap,
    };
    // Check if current entity map isn't empty
    const keysList = Object.keys(newEntityMap);
    if (keysList && keysList.length) {
      // Assign next entity index
      nextEntityIndex = keysList.length;
      // Check each current entity, to update files
      if (filesList) {
        for (let i = 0; i < keysList.length; i++) {
          const entityItem = newEntityMap[i];

          if (!entityItem?.fileId && !entityItem?.data?.fileId) {
            const file = filesList.find((file) => {
              let a = false;
              let b = false;

              // Check if undefined === undefined then false
              a = file.uid || entityItem.data.uid ? file.uid === entityItem.data.uid : a;
              b = file.file?.uid || entityItem.data.uid ? file.file?.uid === entityItem.data.uid : b;

              return a || b;
            });
            if (file) {
              newEntityMap[i].data.fileId = file.fileId;
              newEntityMap[i].data.file = undefined;
            }
          }
        }
      }
    }
  }

  if (blocks) {
    // Check each block for link entities and create them if needed
    blocks.forEach((contentBlock) => {
      let links = null;
      if (contentBlock.type !== 'code-block') {
        links = linkify.match(contentBlock.text);
      }

      if (!links) {
        // If there are no links found, save block and return
        newBlocks.push(contentBlock);
        return;
      }

      const entityRanges = contentBlock.entityRanges ? contentBlock.entityRanges : [];
      // Process each link to check if there are existed entity for it
      for (let i = 0; i < links.length; i += 1) {
        const linkItem = links[i];

        // Check if link entity already existed
        if (entityRanges && entityRanges.length) {
          let entityExists = false;
          // Check each entity range of content block
          entityRanges.forEach((entity) => {
            if (entity.offset === linkItem.index) {
              entityExists = entity.key;
            }
          });

          // Skip current link if entity already exists
          if (entityExists || (entityExists === 0 && newEntityMap[entityExists])) {
            newEntityMap[entityExists].data.url = linkItem.url;
            continue;
          }
        }

        // If there are no entity for link, create it

        // Add new entity to the map object
        newEntityMap[nextEntityIndex] = {
          type: 'LINK',
          mutability: 'MUTABLE',
          data: {
            url: linkItem.url,
          },
        };
        // Add entity range to the block entity ranges
        entityRanges.push({
          key: nextEntityIndex,
          length: linkItem.lastIndex - linkItem.index,
          offset: linkItem.index,
        });
        nextEntityIndex++;
      }

      // Save new block
      newBlocks.push({
        ...contentBlock,
        entityRanges,
      });
    });
  }

  // Remove empty lines from the end of the content
  if (newBlocks && newBlocks.length) {
    let lastBlockContent = newBlocks[newBlocks.length - 1];

    while (lastBlockContent && lastBlockContent.text === '' && lastBlockContent.type === 'unstyled') {
      // Prevent empty block removing for atomic blocks, because it can cause editor error
      if (newBlocks[newBlocks.length - 2] && newBlocks[newBlocks.length - 2].type === BLOCK_TYPE.ATOMIC) {
        break;
      }
      newBlocks.pop();
      lastBlockContent = newBlocks[newBlocks.length - 1];
    }
  }

  editorFilesCount = 0;
  // Return new content
  return {
    blocks: newBlocks,
    entityMap: newEntityMap,
  };
};

/**
 * Function handles text pasting to the editor
 *
 * @param text text content
 * @param html html view of text content, if it is rich text
 * @param editorState current editor state
 * @param updateEditorState function to update editor state
 * @returns {boolean}
 */
export const handlePastedText = (text, html, editorState, updateEditorState) => {
  if (html) {
    if (pasteTextTable({ editorState, html, updateEditorState })) {
      return true;
    }

    if (
      html.indexOf('google-sheets-html-origin') !== -1 || // Google sheets several cells pasting
      html.indexOf('mso-data-placement') !== -1 || // Google sheets one cell pasting
      html.indexOf('Microsoft Excel') !== -1 ||
      html.indexOf('LibreOffice') !== -1
    ) {
      // Check if this is pasting from google sheets to remove additional html layout
      const currentContent = editorState.getCurrentContent();
      const selection = editorState.getSelection();

      let newContent = null;
      const textParts = text.split(/\r\n?|\n/g);

      if (textParts && textParts.length) {
        textParts.forEach((part) => {
          // Paste line to the content
          if (!newContent) {
            newContent = Modifier.insertText(currentContent, selection, part);
          } else {
            newContent = Modifier.insertText(newContent, newContent.getSelectionAfter(), part);
          }

          // Add line break
          newContent = Modifier.splitBlock(newContent, newContent.getSelectionAfter());
        });
      }

      // Update editor state
      const newEditorState = EditorState.push(editorState, newContent, 'insert-fragment');
      updateEditorState(newEditorState);

      // Return true to disable default pasting
      return true;
    }
  }

  // Get current block type
  const currentBlockType = RichUtils.getCurrentBlockType(editorState);
  if (currentBlockType === BLOCK_TYPE.BLOCKQUOTE) {
    // Handle pasting of a text to the blockquote to prevent blocks splitting
    const newContent = Modifier.insertText(editorState.getCurrentContent(), editorState.getSelection(), text);

    // Update editor state
    const newEditorState = EditorState.push(editorState, newContent, 'insert-fragment');
    updateEditorState(newEditorState);

    // Return true to disable default pasting
    return true;
  }

  // Otherwise use default pasting
  return false;
};

/**
 * Function performs removing of all styles from editor content
 * @param editorState
 * @param updateEditorState
 */
export const removeTextFormatting = (editorState, updateEditorState) => {
  let newEditorState = editorState;
  // Handle inline styles
  newEditorState = removeInlineStyles(newEditorState);

  if (RichUtils.getCurrentBlockType(editorState) !== BLOCK_TYPE.TABLE_ROW_ITEM) {
    newEditorState = removeBlockStyles(newEditorState);
  }

  updateEditorState(newEditorState);
};

/**
 * Function removes inline style from all editor's text
 * @param editorState
 * @returns {EditorState}
 */
const removeInlineStyles = (editorState) => {
  const currentContent = editorState.getCurrentContent();
  const inlineStyles = ['BOLD', 'ITALIC', 'UNDERLINE'];
  // Selection for all editor's text
  /*const selection = editorState.getSelection().merge({
    anchorKey: currentContent.getFirstBlock().getKey(),
    anchorOffset: 0,

    focusOffset: currentContent.getLastBlock().getText().length,
    focusKey: currentContent.getLastBlock().getKey(),
  });*/
  // Remove each style specified in `inlineStyles`
  const contentWithoutStyles = inlineStyles.reduce(
    (source, style) => Modifier.removeInlineStyle(source, editorState.getSelection(), style),
    currentContent,
  );

  // Return updated editor state
  return EditorState.push(editorState, contentWithoutStyles, 'change-inline-style');
};

/**
 * Function removes block styles from all editor's blocks
 * @param editorState
 * @returns {EditorState}
 */
const removeBlockStyles = (editorState) => {
  const currentContent = editorState.getCurrentContent();
  const contentWithoutStyles = Modifier.setBlockType(currentContent, editorState.getSelection(), 'unstyled');

  return EditorState.push(editorState, contentWithoutStyles, 'change-block-type');
};

let editorFilesCount = 0;
/**
 * Function handles pasting of files into draft editor.
 * @param files
 * @param uploadFile
 * @param editorState
 * @param updateEditorState
 * @param filesCount
 */
export const handlePastedFiles = (files, uploadFile, editorState, updateEditorState, filesCount) => {
  let newEditorState = editorState;
  let currentContent;

  // Check files count to prevent issues with files indexes
  if (editorFilesCount < filesCount) {
    editorFilesCount = filesCount;
  }

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (file.type && file.type.indexOf('image') !== -1) {
      file.uid = 'editor-image-' + editorFilesCount;
      editorFilesCount++;
      uploadFile(file, files);

      currentContent = newEditorState.getCurrentContent();
      currentContent = currentContent.createEntity('IMAGE', 'IMMUTABLE', {
        uid: file.uid,
        name: file.name,
        file,
      });
      const entityKey = currentContent.getLastCreatedEntityKey();
      newEditorState = EditorState.set(newEditorState, {
        currentContent,
      });
      newEditorState = AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');
    }
  }

  updateEditorState(newEditorState);
};

/**
 * Function handles files dropping to the editor.
 *
 * @param selection
 * @param files
 * @param uploadFile
 * @param editorState
 * @param updateEditorState
 * @param filesCount
 */
export const handleDroppedFiles = (selection, files, uploadFile, editorState, updateEditorState, filesCount) => {
  let newEditorState = editorState;
  let currentContent;

  // Check files count to prevent issues with files indexes
  if (editorFilesCount < filesCount) {
    editorFilesCount = filesCount;
  }

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    if (file.type && file.type.indexOf('image') !== -1) {
      file.uid = 'editor-image-' + editorFilesCount;
      editorFilesCount++;
      uploadFile(file, files);

      currentContent = newEditorState.getCurrentContent();
      currentContent = currentContent.createEntity('IMAGE', 'IMMUTABLE', {
        uid: file.uid,
        name: file.name,
        file,
      });
      const entityKey = currentContent.getLastCreatedEntityKey();
      newEditorState = EditorState.set(newEditorState, {
        currentContent,
      });
      newEditorState = AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');
    }
  }

  updateEditorState(newEditorState);
};

/**
 * Function handles uploading of specified file and adding its preview to the editor content.
 *
 * @param file
 * @param editorState
 * @param updateEditorState
 * @param uploadFile
 */
export const handleFileUploading = (file, editorState, updateEditorState, uploadFile) => {
  let newEditorState = editorState;
  let currentContent;

  if (file.type && file.type.indexOf('image') !== -1) {
    file.uid = 'editor-image-' + editorFilesCount;
    editorFilesCount++;
    uploadFile(file, [file]);

    currentContent = newEditorState.getCurrentContent();
    currentContent = currentContent.createEntity('IMAGE', 'IMMUTABLE', {
      uid: file.uid,
      name: file.name,
      file,
    });
    const entityKey = currentContent.getLastCreatedEntityKey();
    newEditorState = EditorState.set(newEditorState, {
      currentContent,
    });
    newEditorState = AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');

    updateEditorState(newEditorState);
  }
};

/**
 * Function handles pasting of an image to editor from internal component.
 * @param file
 * @param editorState
 * @param updateEditorState
 */
export const handleInternalImagePaste = (file, editorState, updateEditorState) => {
  let newEditorState = editorState;
  let currentContent;

  if (file?.get('type') && file.get('type').indexOf('image') !== -1) {
    editorFilesCount++;

    currentContent = newEditorState.getCurrentContent();
    currentContent = currentContent.createEntity('IMAGE', 'IMMUTABLE', {
      uid: file.get('uid'),
      name: file.get('name'),
      file: file.get('file'),
      fileId: file.get('fileId'),
    });
    const entityKey = currentContent.getLastCreatedEntityKey();
    newEditorState = EditorState.set(newEditorState, {
      currentContent,
    });
    newEditorState = AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, ' ');

    updateEditorState(newEditorState);
  }
};
